import MealPlanApiClient from "utils/mealPlanApiClient";

// Form state interface
export interface FormState {
  weight: string;
  height: string;
  age: string;
  sex: string;
  activityLevel: string;
  dietaryRestrictions: string[];
  allergies: string[];
  additionalInfo: string;
  days: number;
}

// Meal plan data interfaces aligned with Pydantic schema
export interface Macros {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

export interface ConstraintsWithMacros {
  constraints: Record<string, any>;
  macros?: Macros;
}

// API response ingredient format
export interface ApiIngredientPortion {
  weight: {
    unit: string;
    value: number;
  };
  name: string;
  quantity: number;
  suggestedQuantity?: number[];
}

export interface ApiNutritionPreview {
  portion: ApiIngredientPortion;
  calories: number;
  carbs: number;
  fat: number;
  protein: number;
  fiber?: number;
}

export interface ApiIngredient {
  type: string;
  displayName: string;
  shortName?: string;
  longName?: string;
  brandName?: string;
  nutritionPreview: ApiNutritionPreview;
  // Other API fields we don't need to use but might be present
  [key: string]: any;
}

// UI format for ingredients
export interface RecipeIngredient {
  ingredientName: string;
  portionQuantity: number;
  portionSize: string;
  weightGrams: number;
  // Additional fields for frontend display
  calories?: number;
  carbs?: number;
  fat?: number;
  protein?: number;
  fiber?: number;
  // Keep the original API data for reference if needed
  apiData?: ApiIngredient;
}

export interface Recipe {
  name: string;
  ingredients: (RecipeIngredient | ApiIngredient)[];
  preparation: string;
  // API may include macros directly
  macros?: Macros;
  // Additional fields for frontend
  rejected?: boolean;
  totalNutrition?: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface Meal {
  recipes: Recipe[];
  // Additional field for frontend
  totalNutrition?: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface DayMealPlan {
  breakfast: Meal;
  lunch: Meal;
  dinner: Meal;
  snack: Meal;
  // Day's macros (coming from the API)
  macros?: Macros;
  // Additional field for frontend
  totalNutrition?: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface MealPlanData {
  constraints: ConstraintsWithMacros;
  mealPlanDays: DayMealPlan[];
}

// Helper functions to convert between API and UI formats
export const convertApiIngredientToUi = (
  apiIngredient: ApiIngredient
): RecipeIngredient => {
  const { nutritionPreview, displayName } = apiIngredient;
  const {
    portion,
    calories,
    carbs,
    fat,
    protein,
    fiber = 0,
  } = nutritionPreview;

  return {
    ingredientName: displayName,
    portionQuantity: portion.quantity,
    portionSize: portion.name,
    weightGrams: portion.weight.value,
    calories,
    carbs,
    fat,
    protein,
    fiber,
    apiData: apiIngredient,
  };
};

export const isApiIngredient = (
  ingredient: any
): ingredient is ApiIngredient => {
  return (
    ingredient &&
    typeof ingredient === "object" &&
    "displayName" in ingredient &&
    "nutritionPreview" in ingredient
  );
};

// Detailed nutrition interfaces for full meal plan
export interface IngredientWithNutrition {
  name: string;
  amount: string;
  calories: number;
  carbs: number;
  fat: number;
  protein: number;
}

export interface DetailedRecipe extends Omit<Recipe, "ingredients"> {
  ingredients: IngredientWithNutrition[];
  totalNutrition: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface DetailedMeal {
  recipes: DetailedRecipe[];
  totalNutrition: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface DetailedMealPlanDay {
  breakfast: DetailedMeal;
  lunch: DetailedMeal;
  dinner: DetailedMeal;
  snack: DetailedMeal;
  // Day's macros (coming from the API)
  macros?: Macros;
  totalNutrition: {
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  };
}

export interface FullMealPlanData {
  constraints: ConstraintsWithMacros;
  mealPlanDays: DetailedMealPlanDay[];
}

// Props interfaces
export interface MealPlanFormProps {
  onGenerateMealPlan: (formState: FormState) => void;
  onGeneratePreview: (formState: FormState) => void;
  isLoading: boolean;
}

export interface MealPlanResultsProps {
  isLoading: boolean;
  onImportMealPlan?: (importedData: MealPlanData) => void;
}

export interface MealPlanPreviewProps {
  mealPlanData: MealPlanData;
  onRejectRecipe?: (
    dayIndex: number,
    mealType: "breakfast" | "lunch" | "dinner" | "snack",
    recipeIndex: number
  ) => void;
  onGenerateFull?: () => void;
  onBack: () => void;
  disableGenerate?: boolean;
  showFullDetails?: boolean;
  isLoading?: boolean;
  onImportMealPlan?: (importedData: MealPlanData) => void;
  apiClient?: MealPlanApiClient | null;
}

export interface FullMealPlanProps {
  mealPlanData: MealPlanData;
  onBack: () => void;
  isLoading?: boolean;
  apiClient?: MealPlanApiClient | null;
}
